import {
  FEEDBACK_SCREENSHOT_MAX_BYTES,
  FEEDBACK_SCREENSHOT_MAX_PIXELS,
  FEEDBACK_SCREENSHOT_MAX_SIDE,
  FEEDBACK_SCREENSHOT_TYPE,
} from "@esi/shared";
import { toSvg } from "html-to-image";
import {
  CONTENT_ATTRIBUTE,
  CONTENT_REGIONS,
  type ContentRegion,
  isContentRegion,
} from "./content-regions.ts";

/**
 * The feedback screenshot (openspec change `in-app-feedback`, design D4): a DOM capture of the
 * app's root (`#root`: dialogs, menus and toasts live in portals outside it, so the feedback
 * dialog itself is never in the picture) with every content region masked.
 *
 * Masking happens twice, so a marked region never reaches the PNG:
 * 1. While cloning, nothing under a masked element is cloned at all (`filter`): its descendants'
 *    text, images, attributes, input values and pseudo-elements never enter the clone, and
 *    their images are never fetched.
 * 2. The masked element itself is cloned (its box keeps the layout) and then, in the serialised
 *    SVG, replaced by an empty `div` that keeps only its inline style and region name, painted
 *    with a neutral fill: its own `title`, `aria-label`, `alt`, `value`, classes (the clone's
 *    pseudo-element rules hang on them) and children are gone. Its own image, if it has one (a
 *    marked `<img>`, a background), is fetched while cloning and discarded with it. An inline
 *    marker (whose computed size is `auto`) gets the size of its rendered box.
 * The SVG is then drawn on a canvas and re-encoded as a PNG, which carries no metadata.
 */

const XHTML = "http://www.w3.org/1999/xhtml";
const SVG_PREFIX = "data:image/svg+xml;charset=utf-8,";
/** Transparent 1×1 PNG: an image that cannot be embedded (cross-origin) renders as nothing. */
const PLACEHOLDER =
  "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mNkYAAAAAYAAjCB0C8AAAAASUVORK5CYII=";
/** The neutral block: a translucent grey that reads on light and dark surfaces. */
export const MASK_STYLE =
  "background: rgba(128, 128, 128, 0.32) !important; background-image: none !important;" +
  " border-color: transparent !important; box-shadow: none !important;" +
  " color: transparent !important; outline: none !important; overflow: hidden !important;";

/** Whether `el` is a marker the capture masks: any marker whose region was not unmasked. */
function masks(el: Element, unmasked: ReadonlySet<ContentRegion>): boolean {
  if (!el.hasAttribute(CONTENT_ATTRIBUTE)) {
    return false;
  }
  const region = el.getAttribute(CONTENT_ATTRIBUTE);
  // An unknown name is never unmaskable.
  return !isContentRegion(region) || !unmasked.has(region);
}

/**
 * The clone filter: false for any node with a masked ancestor, so nothing under a masked region
 * is cloned (html-to-image calls it for every node but the root, text nodes included).
 */
export function captureFilter(unmasked: ReadonlySet<ContentRegion>) {
  return (node: Node): boolean => {
    for (let el = node.parentElement; el; el = el.parentElement) {
      if (masks(el, unmasked)) {
        return false;
      }
    }
    return true;
  };
}

/**
 * The regions that would actually show if unmasked, given `unmasked`: those with an element no
 * masked region encloses. A region inside a masked one (`messages` inside `page`) is never drawn,
 * whatever its own choice. In {@link CONTENT_REGIONS} order.
 */
export function reachableRegions(
  root: ParentNode,
  unmasked: ReadonlySet<ContentRegion>,
): ContentRegion[] {
  const keep = captureFilter(unmasked);
  const found = new Set<ContentRegion>();
  for (const el of root.querySelectorAll(`[${CONTENT_ATTRIBUTE}]`)) {
    const region = el.getAttribute(CONTENT_ATTRIBUTE);
    if (isContentRegion(region) && keep(el)) {
      found.add(region);
    }
  }
  return CONTENT_REGIONS.filter((r) => found.has(r));
}

/** The content regions on screen in `root`, in {@link CONTENT_REGIONS} order (`page` first). */
export function regionsIn(root: ParentNode): ContentRegion[] {
  const found = new Set<ContentRegion>();
  for (const el of root.querySelectorAll(`[${CONTENT_ATTRIBUTE}]`)) {
    const region = el.getAttribute(CONTENT_ATTRIBUTE);
    if (isContentRegion(region)) {
      found.add(region);
    }
  }
  return CONTENT_REGIONS.filter((r) => found.has(r));
}

/**
 * Replaces every masked element of a serialised capture with an empty block of the same box
 * (an inline one's from {@link BOX_ATTRIBUTE}).
 * Throws when the SVG cannot be parsed: a capture that cannot be checked is never used.
 */
export function maskSvg(svg: string, unmasked: ReadonlySet<ContentRegion>): string {
  const doc = new DOMParser().parseFromString(svg, "image/svg+xml");
  if (doc.querySelector("parsererror") || doc.documentElement.nodeName !== "svg") {
    throw new Error("the capture is not a valid SVG");
  }
  for (const el of [...doc.querySelectorAll(`[${CONTENT_ATTRIBUTE}]`)]) {
    // Inside an already replaced block: detached, skip.
    if (!doc.documentElement.contains(el) || !masks(el, unmasked)) {
      continue;
    }
    const block = doc.createElementNS(XHTML, "div");
    const style = el.getAttribute("style") ?? "";
    const inline = /(?:^|;)\s*display:\s*inline\s*(?:;|$)/.test(style);
    // An inline box's computed width and height are `auto`: the live box's size keeps it.
    const [width, height] = (el.getAttribute(BOX_ATTRIBUTE) ?? "").split(",").map(Number);
    const box =
      inline && width && height
        ? `width: ${width}px !important; height: ${height}px !important; vertical-align: bottom !important; `
        : "";
    block.setAttribute(
      "style",
      `${style}${style.trim().endsWith(";") || style.trim() === "" ? "" : ";"} ${inline ? `display: inline-block !important; ${box}` : ""}${MASK_STYLE}`,
    );
    block.setAttribute(CONTENT_ATTRIBUTE, el.getAttribute(CONTENT_ATTRIBUTE) ?? "");
    el.replaceWith(block);
  }
  restoreScroll(doc);
  return new XMLSerializer().serializeToString(doc);
}

/** An inline marker's rendered size, carried into the clone (its computed size is `auto`). */
export const BOX_ATTRIBUTE = "data-esi-box";
/** Where a scrolled element was scrolled to, carried into the clone (html-to-image drops it). */
export const SCROLL_ATTRIBUTE = "data-esi-scroll";
/** The layout a wrapper takes from its scroll container, so its children lay out as before. */
const LAYOUT = [
  "display",
  "flex-direction",
  "flex-wrap",
  "gap",
  "row-gap",
  "column-gap",
  "align-items",
  "justify-content",
  "grid-template-columns",
  "grid-template-rows",
];

/**
 * Shows each scrolled element's visible part in the clone: its children move into a wrapper
 * shifted by the scroll offset (the element keeps its box and clips as before).
 */
function restoreScroll(doc: Document): void {
  for (const el of [...doc.querySelectorAll(`[${SCROLL_ATTRIBUTE}]`)]) {
    const [top, left] = (el.getAttribute(SCROLL_ATTRIBUTE) ?? "")
      .split(",")
      .map((n) => Math.max(0, Number(n) || 0));
    el.removeAttribute(SCROLL_ATTRIBUTE);
    if (!top && !left) {
      continue;
    }
    const style = el.getAttribute("style") ?? "";
    const layout = LAYOUT.flatMap((name) => {
      const m = new RegExp(`(?:^|;)\\s*${name}:\\s*([^;]+)`).exec(style);
      return m ? [`${name}: ${m[1]?.trim()}`] : [];
    });
    const wrapper = doc.createElementNS(XHTML, "div");
    wrapper.setAttribute(
      "style",
      [
        ...layout,
        "min-width: 100%",
        `transform: translate(${-(left ?? 0)}px, ${-(top ?? 0)}px)`,
      ].join("; "),
    );
    while (el.firstChild) {
      wrapper.append(el.firstChild);
    }
    el.append(wrapper);
  }
}

/**
 * Marks, for the clone, every scrolled element under `root` with its offsets and every inline
 * content marker with its rendered size; returns the undo.
 */
export function markLayout(root: HTMLElement): () => void {
  const marked: [Element, string][] = [];
  const mark = (el: Element, name: string, value: string) => {
    el.setAttribute(name, value);
    marked.push([el, name]);
  };
  for (const el of [root, ...root.querySelectorAll("*")]) {
    // A form control's clone takes its value as its text: a wrapper would hide it.
    if ((el.scrollTop > 0 || el.scrollLeft > 0) && !el.matches("textarea, input, select")) {
      mark(el, SCROLL_ATTRIBUTE, `${Math.round(el.scrollTop)},${Math.round(el.scrollLeft)}`);
    }
    if (el.hasAttribute(CONTENT_ATTRIBUTE) && getComputedStyle(el).display === "inline") {
      const rect = el.getBoundingClientRect();
      mark(el, BOX_ATTRIBUTE, `${Math.ceil(rect.width)},${Math.ceil(rect.height)}`);
    }
  }
  return () => {
    for (const [el, name] of marked) {
      el.removeAttribute(name);
    }
  };
}

/** html-to-image's SVG data URL, decoded. */
export function svgOf(dataUrl: string): string {
  if (!dataUrl.startsWith(SVG_PREFIX)) {
    throw new Error("unexpected capture format");
  }
  return decodeURIComponent(dataUrl.slice(SVG_PREFIX.length));
}

export interface Screenshot {
  blob: Blob;
  width: number;
  height: number;
}

export interface CaptureOptions {
  unmasked?: ReadonlySet<ContentRegion>;
  maxBytes?: number;
  /** Device pixels per CSS pixel, at most 2. */
  pixelRatio?: number;
}

/** The masked SVG of `root` (what is drawn), for the capture and its tests. */
export async function maskedSvg(
  root: HTMLElement,
  unmasked: ReadonlySet<ContentRegion> = new Set(),
): Promise<string> {
  const unmark = markLayout(root);
  let dataUrl: string;
  try {
    dataUrl = await toSvg(root, {
      filter: captureFilter(unmasked),
      imagePlaceholder: PLACEHOLDER,
      width: root.clientWidth,
      height: root.clientHeight,
    });
  } finally {
    unmark();
  }
  return maskSvg(svgOf(dataUrl), unmasked);
}

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error("the capture could not be drawn"));
    img.src = src;
  });
}

function toPng(canvas: HTMLCanvasElement): Promise<Blob> {
  return new Promise((resolve, reject) =>
    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error("PNG encoding failed"))),
      FEEDBACK_SCREENSHOT_TYPE,
    ),
  );
}

/**
 * Draws `img` at `scale` and encodes a PNG (a canvas export holds pixels only: no EXIF, no text
 * chunks), halving the area until it fits `maxBytes`.
 */
export async function encodePng(
  img: CanvasImageSource,
  width: number,
  height: number,
  options: { scale: number; maxBytes: number },
): Promise<Screenshot> {
  let scale = options.scale;
  for (let attempt = 0; attempt < 6; attempt++) {
    const canvas = document.createElement("canvas");
    canvas.width = Math.max(1, Math.round(width * scale));
    canvas.height = Math.max(1, Math.round(height * scale));
    const context = canvas.getContext("2d");
    if (!context) {
      throw new Error("no canvas");
    }
    context.drawImage(img, 0, 0, canvas.width, canvas.height);
    const blob = await toPng(canvas);
    if (blob.size <= options.maxBytes) {
      return { blob, width: canvas.width, height: canvas.height };
    }
    scale *= Math.SQRT1_2;
  }
  throw new Error("the screenshot stays over the size limit");
}

/**
 * The device-pixel scale of a capture: at most 2, and within the confirm's bounds
 * ({@link FEEDBACK_SCREENSHOT_MAX_SIDE} a side, {@link FEEDBACK_SCREENSHOT_MAX_PIXELS} in all).
 */
export function captureScale(width: number, height: number, pixelRatio: number): number {
  const w = Math.max(1, width);
  const h = Math.max(1, height);
  return Math.min(
    2,
    pixelRatio,
    // Rounded down a hair, so `Math.round` in the encoder never crosses a bound.
    (FEEDBACK_SCREENSHOT_MAX_SIDE - 1) / w,
    (FEEDBACK_SCREENSHOT_MAX_SIDE - 1) / h,
    Math.sqrt((FEEDBACK_SCREENSHOT_MAX_PIXELS * 0.999) / (w * h)),
  );
}

/** Captures `root` masked, as a PNG of at most {@link FEEDBACK_SCREENSHOT_MAX_BYTES}. */
export async function captureScreenshot(
  root: HTMLElement,
  options: CaptureOptions = {},
): Promise<Screenshot> {
  const svg = await maskedSvg(root, options.unmasked);
  // A data URL, as html-to-image draws it: browsers do not taint a canvas for it.
  const img = await loadImage(`${SVG_PREFIX}${encodeURIComponent(svg)}`);
  const width = root.clientWidth;
  const height = root.clientHeight;
  return encodePng(img, width, height, {
    scale: captureScale(width, height, options.pixelRatio ?? window.devicePixelRatio ?? 1),
    maxBytes: options.maxBytes ?? FEEDBACK_SCREENSHOT_MAX_BYTES,
  });
}
